// The open overlays (Modals and Popovers) in the order they were opened, the last one on top. Only the topmost one
// handles Escape: a Popover opened in a Modal closes first, then the Modal, and a Popup over a Modal closes
// without the Modal under it. Also holds the body scroll lock shared by nested Modals.

export interface Overlay {
    kind: 'modal' | 'popover'
    /** Called with an Escape keydown when this is the topmost overlay that takes Escape */
    onEscape: (event: KeyboardEvent) => void
    /**
     * False lets Escape through to the overlay below (a disabled or hidden Popover, a hover tooltip). A Modal always
     * takes it, also when it doesn't close on Escape, so the Modal under it stays open.
     */
    takesEscape?: () => boolean
}

const overlays: Overlay[] = []

// Escape events handled by an overlay
const handledEscapes = new WeakSet<Event>()

/** Puts the overlay on top (again) */
export function openOverlay(overlay: Overlay) {
    closeOverlay(overlay)
    overlays.push(overlay)
}

export function closeOverlay(overlay: Overlay) {
    const index = overlays.indexOf(overlay)

    if (index !== -1) {
        overlays.splice(index, 1)
    }
}

/** Number of open Modals under this one, 0 for the first */
export function getModalLevel(overlay: Overlay) {
    const index = overlays.indexOf(overlay)
    const below = index === -1 ? overlays : overlays.slice(0, index)

    return below.filter((other) => other.kind === 'modal').length
}

/** Whether no Modal was opened after this one (Popovers don't count: a Select open in it doesn't take it over) */
export function isTopmostModal(overlay: Overlay) {
    const modals = overlays.filter((other) => other.kind === 'modal')

    return modals[modals.length - 1] === overlay
}

/** Whether this Escape keydown was handled by an overlay (closed a Popover or reached a Modal) */
export function isEscapeHandled(event: Event) {
    return handledEscapes.has(event)
}

// Capture on window runs before every document listener and React's handlers, whatever order they were added in
function onKeyDown(event: KeyboardEvent) {
    if (event.key !== 'Escape' || handledEscapes.has(event)) {
        return
    }

    const overlay = [...overlays].reverse().find((overlay) => overlay.takesEscape?.() ?? true)

    if (overlay) {
        handledEscapes.add(event)
        overlay.onEscape(event)
    }
}

if (typeof window !== 'undefined') {
    window.addEventListener('keydown', onKeyDown, true)
}

let scrollLocks = 0
let bodyStyleBeforeLock: {overflow: string, paddingRight: string, paddingRightPriority: string} = null

/**
 * Stops the page behind a Modal from scrolling until the returned function is called. Nested Modals share the lock:
 * the body is restored when the last one closes. The scrollbar's width is added as padding so nothing moves.
 */
export function lockBodyScroll() {
    if (typeof document === 'undefined') {
        return () => {}
    }

    if (scrollLocks === 0) {
        const {style} = document.body
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth

        bodyStyleBeforeLock = {overflow: style.overflow, paddingRight: style.paddingRight, paddingRightPriority: style.getPropertyPriority('padding-right')}

        style.overflow = 'hidden'

        if (scrollbarWidth > 0) {
            // style.css sets the body padding with !important
            style.setProperty('padding-right', `${scrollbarWidth}px`, 'important')
        }
    }

    scrollLocks++

    let unlocked = false

    return () => {
        if (unlocked) {
            return
        }

        unlocked = true
        scrollLocks--

        if (scrollLocks === 0 && bodyStyleBeforeLock) {
            const {style} = document.body

            style.overflow = bodyStyleBeforeLock.overflow
            style.setProperty('padding-right', bodyStyleBeforeLock.paddingRight, bodyStyleBeforeLock.paddingRightPriority)
            bodyStyleBeforeLock = null
        }
    }
}
